/**
 * Property module API contracts (docs/08: "API contracts typed end-to-end —
 * Zod schemas shared to client"). The server validates against these at the
 * boundary (gate 5); the web app imports the same types, so drift is a
 * compile error.
 */

import { z } from 'zod';

import { canonicalTimeZone } from '../time-zones.ts';

import {
  addressComponents,
  addressHasAnyComponent,
  addressHasContent,
  type AddressRow,
  type PhoneRow,
} from './address.ts';
import { partialUpdateSchema } from './partial-update.ts';
import { DEFAULT_PAGE_SIZE } from './table-layouts.ts';

/** Fields a person may write. Status is NEVER writable here — lifecycle moves
 *  only through the dedicated inactivate endpoint, so an edit can't silently
 *  resurrect or retire a record (docs/02 soft lifecycle). */
/**
 * A field that names WHO does something: a vendor TKC contracts, TKC itself, or
 * a name nobody has turned into a vendor record yet (O-24, migration 0076).
 *
 * THREE STATES, NOT TWO, and the third is the reason this exists. Matt:
 * "convert it now but still allow the user to also select 'TKC Internal' or type
 * free text — this needs some flexibility". These fields held free text for
 * years; a column that only accepts a vendor id would make every one of those
 * values unsaveable the moment somebody opened the form.
 *
 * Absent from a write means "leave it alone"; `null` means "not stated", which
 * is deliberately NOT the same as `internal` — "nobody has said" must not read
 * as "we handle it ourselves".
 */
export const PARTY_REF_KINDS = ['vendor', 'internal', 'text'] as const;
export type PartyRefKind = (typeof PARTY_REF_KINDS)[number];

/** The label for the internal state. One spelling, shared by API and UI. */
export const PARTY_INTERNAL_LABEL = 'TKC Internal';

/**
 * A discriminated union, so the impossible combinations cannot be constructed
 * at all rather than being caught later: no vendor id on an `internal`, no
 * leftover text on a `vendor`. The database's shape CHECK (0076) says the same
 * thing again at the far end, because a contract is not a constraint.
 */
export const partyRefWriteSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('vendor'), vendorId: z.uuid() }),
  z.object({ kind: z.literal('internal') }),
  z.object({ kind: z.literal('text'), text: z.string().trim().min(1).max(200) }),
]);
export type PartyRefWrite = z.infer<typeof partyRefWriteSchema>;

/**
 * A party as it READS. Flat rather than a union, because every consumer wants
 * "what do I display" and a resolved `vendorName` is the answer for two of the
 * three states — a screen must never show a uuid, and neither must an audit
 * diff.
 */
export interface PartyRef {
  readonly kind: PartyRefKind;
  readonly vendorId: string | null;
  readonly vendorName: string | null;
  readonly text: string | null;
}

/** What a person reads for a party, in any state. One implementation, so the
 *  detail screen, the form and the audit diff cannot disagree. */
export function partyRefLabel(ref: PartyRef | null): string | null {
  if (ref === null) return null;
  switch (ref.kind) {
    case 'vendor':
      // A vendor whose name did not resolve is a dangling reference, and saying
      // so is better than rendering an empty cell that looks like "not stated".
      return ref.vendorName ?? 'Unknown vendor';
    case 'internal':
      return PARTY_INTERNAL_LABEL;
    case 'text':
      return ref.text;
  }
}

/** 1 = Monday .. 7 = Sunday (ISO), matching the database's `day_of_week`. */
export const DAY_LABELS: readonly { readonly day: number; readonly label: string }[] = [
  { day: 1, label: 'Monday' },
  { day: 2, label: 'Tuesday' },
  { day: 3, label: 'Wednesday' },
  { day: 4, label: 'Thursday' },
  { day: 5, label: 'Friday' },
  { day: 6, label: 'Saturday' },
  { day: 7, label: 'Sunday' },
];

/**
 * One day's hours. Either open round the clock, or a window with both ends —
 * a start with no end is a half-filled form, not data, and the database's CHECK
 * says the same thing.
 *
 * Times are 24-hour `HH:MM` strings, LOCAL to the property (O-25). `HH:MM`
 * because that is exactly what an `<input type="time">` produces, so nothing
 * has to parse or reformat on the way through.
 */
export const hoursDaySchema = z
  .object({
    dayOfWeek: z.number().int().min(1).max(7),
    opensAt: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
      .nullish(),
    closesAt: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
      .nullish(),
    isOpen24h: z.boolean().default(false),
  })
  .refine(
    (day) =>
      day.isOpen24h
        ? (day.opensAt ?? null) === null && (day.closesAt ?? null) === null
        : (day.opensAt ?? null) !== null && (day.closesAt ?? null) !== null,
    { message: 'Give both a start and an end, or mark the day open 24 hours' },
  );
export type HoursDay = z.infer<typeof hoursDaySchema>;

/**
 * What a property does on one holiday. `closed` and `open_24h` carry no times;
 * `window` carries both. Three states in one field so two booleans cannot
 * contradict each other, matching the database's CHECK.
 */
export const holidayHoursSchema = z
  .object({
    holidayId: z.uuid(),
    status: z.enum(['closed', 'open_24h', 'window']),
    opensAt: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
      .nullish(),
    closesAt: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
      .nullish(),
    notes: z.string().trim().max(500).nullish(),
  })
  .refine(
    (entry) =>
      entry.status === 'window'
        ? (entry.opensAt ?? null) !== null && (entry.closesAt ?? null) !== null
        : (entry.opensAt ?? null) === null && (entry.closesAt ?? null) === null,
    { message: 'Give both a start and an end, or choose closed or open 24 hours' },
  );
export type HolidayHours = z.infer<typeof holidayHoursSchema>;

/** A TKC holiday, with the date it falls on for the years the UI is showing. */
export interface HolidayRow {
  readonly id: string;
  readonly systemKey: string;
  readonly name: string;
  readonly ruleKind: 'fixed_date' | 'nth_weekday' | 'last_weekday';
  readonly ruleMonth: number;
  readonly ruleDay: number | null;
  readonly ruleWeekday: number | null;
  readonly ruleNth: number | null;
  readonly ruleOffsetDays: number;
  readonly observanceShift: 'none' | 'nearest_weekday';
  /** Year -> the date TKC pinned, when the rule was overruled. */
  readonly overrides: Readonly<Record<number, string>>;
}

/**
 * The crop rectangle, in STORED-SOURCE pixels (punch 4.3, docs/11 F-55).
 *
 * Integers only, and integers all the way down: the accepted rectangle is
 * hashed into the photo's derived key, and a float would make that hash depend
 * on how a number happened to serialize. The server normalizes what it receives
 * to an exact 16:9 rectangle — the client is not asked to land on multiples of
 * 16 — and stores the rectangle it rendered.
 */
export const photoCropRectSchema = z.object({
  x: z.number().int().min(0).max(100_000),
  y: z.number().int().min(0).max(100_000),
  width: z.number().int().positive().max(100_000),
  height: z.number().int().positive().max(100_000),
});
export type PhotoCropRect = z.infer<typeof photoCropRectSchema>;

/** PATCH /api/v1/properties/:id/photo/crop — adjust framing, no re-upload. */
export const photoCropSchema = z.object({
  version: z.number().int().positive(),
  crop: photoCropRectSchema,
});
export type PhotoCropWrite = z.infer<typeof photoCropSchema>;

export const propertyWriteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  // Addresses and phone numbers are SUB-RESOURCES as of 0040 (docs/02 "The
  // address standard"). A property may have a physical address, a mailing
  // address and a billing address; they live in `addresses` and are written
  // through /api/v1/addresses, not here.
  sqFt: z.number().int().positive().max(100_000_000).nullish(),
  yearBuilt: z.number().int().min(1800).max(2100).nullish(),
  typeId: z.uuid().nullish(),
  managementTypeId: z.uuid().nullish(),
  groupId: z.uuid().nullish(),
  yardiId: z.string().trim().max(60).nullish(),
  tkcNumber: z.string().trim().max(60).nullish(),

  // --- The v1 field set (O-21, migration 0015) -----------------------------
  // The owning legal entity. Not the TKC number and not the Yardi id.
  entityId: z.string().trim().max(120).nullish(),
  tkcWorkUrl: z.url().max(500).nullish(),
  buildingHoursNotes: z.string().trim().max(2000).nullish(),
  /**
   * The IANA zone the building stands in (0075, punch 4.1, O-25 reversed).
   *
   * Stored CANONICAL: the value is run through `canonicalTimeZone`, which fixes
   * case and settles the alias split between the two IANA-derived lists this
   * system contains, so one building in two browsers is one value in the column.
   * An empty string clears it — "not stated" is a legitimate answer for a
   * property nobody has confirmed, and better than a guess.
   */
  timeZone: z
    .string()
    .trim()
    .max(64)
    .superRefine((value, ctx) => {
      if (value !== '' && canonicalTimeZone(value) === null) {
        ctx.addIssue({ code: 'custom', message: 'Choose a time zone from the list' });
      }
    })
    .transform((value) => (value === '' ? null : canonicalTimeZone(value)))
    .nullish(),
  // Access tab. Narrative, generous limits — these are what someone reads on a
  // callout, and a truncated warning is worse than none.
  lockboxLocation: z.string().trim().max(500).nullish(),
  accessNotes: z.string().trim().max(4000).nullish(),
  roofHatchNotes: z.string().trim().max(2000).nullish(),
  criticalInformation: z.string().trim().max(4000).nullish(),
  // Security tab. `securityServiceNotes` is narrative and `accessControlSystem`
  // / `securityCameras` describe EQUIPMENT (Brivo, Kastle, twelve exterior
  // cameras) — none of them name a party, so none of them became a picker.
  securityServiceNotes: z.string().trim().max(4000).nullish(),
  securityCameras: z.string().trim().max(2000).nullish(),
  accessControlSystem: z.string().trim().max(200).nullish(),
  // The two that DO name a party (O-24, wave 4.2, migration 0076).
  accessControlManagedBy: partyRefWriteSchema.nullish(),
  alarmMonitoringService: partyRefWriteSchema.nullish(),

  /**
   * The property's MANUAL alternate names, as a complete list (ADR-35).
   *
   * Sent with the property write rather than through endpoints of its own,
   * because the names live in the property form and a form with one explicit
   * Save must not contain controls that take effect immediately. Absent means
   * "leave them alone"; an empty array means "remove them all".
   *
   * Derived names (roman numeral, Yardi id, street number) are never sent: the
   * database owns those (ADR-33) and would put them straight back.
   */
  aliases: z.array(z.string().trim().min(1).max(120)).max(50).optional(),

  /**
   * The week's opening hours (ADR-36), as a complete list. Absent means "leave
   * them alone"; an empty array means "closed every day".
   *
   * A day that is CLOSED is simply not in the list — absent and closed are the
   * same thing for a building, and a row saying "closed" would be a second way
   * to express nothing.
   */
  hours: z.array(hoursDaySchema).max(7).optional(),

  /**
   * Holiday hours, as a complete list. Absent means "leave them alone"; an
   * empty array means "no holiday is special at this property".
   *
   * A holiday NOT in the list follows the weekly grid — adding a holiday to the
   * company list must not silently close a building whose tenants have 24/7
   * access. So there is no "normal" status to also mean nothing.
   */
  holidayHours: z.array(holidayHoursSchema).max(40).optional(),
});
export type PropertyWrite = z.infer<typeof propertyWriteSchema>;

/**
 * Alias sources (migration 0015). `manual` rows are authored by people and
 * survive every recompute; the `derived_*` rows are owned by the app and
 * rebuilt whenever their source field changes. The UI must not offer to edit a
 * derived alias — it would be silently overwritten on the next save.
 */
export const ALIAS_SOURCES = [
  'manual',
  'derived_roman',
  'derived_yardi',
  'derived_street_number',
] as const;
export type AliasSource = (typeof ALIAS_SOURCES)[number];

/**
 * What a DERIVED name was computed from, for the chip beside it ("from TKC
 * number"). `manual` is excluded by the type: a name somebody typed was
 * computed from nothing, and its chip is absent rather than empty.
 *
 * Here rather than on a screen because it was on two, in two shapes — a typed
 * map on the read card and a three-branch ternary on the form — and they had
 * already drifted: the card capitalised "Street number" mid-sentence and the
 * form did not. Each label reads after the word "from", so each is lower case
 * except the two that are proper nouns.
 */
export const ALIAS_DERIVED_FROM_LABEL: Record<Exclude<AliasSource, 'manual'>, string> = {
  derived_roman: 'TKC number',
  derived_yardi: 'Yardi id',
  derived_street_number: 'street number',
};

export interface PropertyAlias {
  readonly id: string;
  readonly alias: string;
  readonly source: AliasSource;
}

/**
 * The ONE address a property may be created with.
 *
 * Addresses are sub-resources and every later change goes through
 * /api/v1/addresses — but a property with no location is not findable in a
 * list, and a create form whose Address tab only works after saving is a form
 * people abandon. So create takes one, files it under the `physical` purpose as
 * the primary, and stops there. No purpose picker: the address you type while
 * creating a building is the building's location.
 */
export const propertyCreateAddressSchema = z.object(addressComponents).refine(
  // An all-blank block stays allowed: the form sends it whether or not anyone
  // typed in it, and the service then writes no address. But a block with ONLY
  // a State or a Suffix is neither blank nor an address — 0040's
  // `addresses_not_empty` ignores both columns, so it reached the CHECK and
  // came back a 500 (L-10). The same rule the address POST already applies.
  (value) => !addressHasAnyComponent(value) || addressHasContent(value),
  { message: 'An address needs at least a street, a city or a ZIP' },
);
export type PropertyCreateAddress = z.infer<typeof propertyCreateAddressSchema>;

export const propertyCreateSchema = propertyWriteSchema
  .extend({
    /** Set after reviewing the duplicate interstitial (docs/02): creating anyway
     *  requires a reason, which is recorded in the audit event. */
    createAnyway: z.boolean().optional(),
    createAnywayReason: z.string().trim().min(3).max(500).optional(),
    address: propertyCreateAddressSchema.optional(),
  })
  // STRICT, for the same reason partialUpdateSchema is: 0040 moved the address
  // off this schema, and a client still posting `city` would otherwise create a
  // property with no address and be told it worked.
  .strict();
export type PropertyCreate = z.infer<typeof propertyCreateSchema>;

export const propertyUpdateSchema = z.object({
  /** Optimistic concurrency (docs/02, docs/03 §2): stale version -> 409. */
  version: z.number().int().positive(),
  changes: partialUpdateSchema(propertyWriteSchema),
});
export type PropertyUpdate = z.infer<typeof propertyUpdateSchema>;

export const inactivateSchema = z.object({
  version: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});

/**
 * Reactivation carries the CHOSEN children to bring back (ADR-41). The UI
 * defaults on the ones that went inactive with the property and leaves the
 * individually retired ones off; the server verifies every id belongs to the
 * property and is inactive.
 */
export const reactivateSchema = z.object({
  version: z.number().int().positive(),
  spaceIds: z.array(z.uuid()).max(500).default([]),
  tenancyIds: z.array(z.uuid()).max(500).default([]),
});
export type ReactivateInput = z.infer<typeof reactivateSchema>;

export const spaceWriteSchema = z.object({
  label: z.string().trim().min(1).max(60),
  sqFt: z.number().int().positive().max(10_000_000).nullish(),
  floor: z.string().trim().max(30).nullish(),
});
export type SpaceWrite = z.infer<typeof spaceWriteSchema>;

/**
 * A space can be CORRECTED (2026-08-25, docs/11 X-9 item 5).
 *
 * There was no edit at all — not in the UI and not in the API, which offered
 * only inactivate and reactivate. So a mistyped suite label, or a floor area
 * re-measured after a fit-out, could only be answered by inactivating the space
 * and adding a new one — which orphans the `tenancy_spaces` rows pointing at it
 * and loses the answer to "who is in 210". Correcting a label is not a lifecycle
 * event; it is the same suite with its name spelled right.
 */
export const spaceUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(spaceWriteSchema),
});

/**
 * A property group's own fields (docs/11 D-2). The group already had a
 * Documents tab, a Notes tab and a History tab and carried a name and a status,
 * so its first tab had nothing to show. v1's group carries the property field
 * set; these are the ones that describe the GROUP rather than its members.
 *
 * Its Federal EIN is not here: it belongs in the codes vault like a property's
 * (ADR-32), and 0035 is what lets the vault accept a group as a subject.
 */
export const groupWriteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullish(),
  typeId: z.uuid().nullish(),
  managementTypeId: z.uuid().nullish(),
  tkcNumber: z.string().trim().max(60).nullish(),
  tkcWorkUrl: z.union([z.literal(''), z.url()]).nullish(),
  yardiId: z.string().trim().max(60).nullish(),
  /** ADR-37 — the Important Details band, same field name on every entity. */
  criticalInformation: z.string().trim().max(4000).nullish(),
});
export type GroupWrite = z.infer<typeof groupWriteSchema>;

export const groupUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(groupWriteSchema),
});

export interface GroupDetail {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly typeId: string | null;
  readonly typeName: string | null;
  /**
   * Whether the stored type is still active (CR-012). False means a kept
   * historical reference: shown and preservable, never offered for a new pick.
   * Null when no type is stored.
   */
  readonly typeActive: boolean | null;
  readonly managementTypeId: string | null;
  readonly managementTypeName: string | null;
  readonly managementTypeActive: boolean | null;
  readonly tkcNumber: string | null;
  readonly tkcWorkUrl: string | null;
  readonly yardiId: string | null;
  readonly criticalInformation: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly updatedAt: string;
  readonly properties: readonly {
    readonly id: string;
    readonly name: string;
    readonly city: string | null;
    readonly state: string | null;
    readonly status: string;
  }[];
}

export const propertyListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  typeId: z.uuid().optional(),
  groupId: z.uuid().optional(),
  favorites: z.enum(['true']).optional(),
  // One key per sortable column in the properties catalog (table-layouts.ts).
  // Whitelisted here and mapped to SQL in the service — never interpolated.
  sort: z
    .enum([
      'name',
      'city',
      'type',
      'sq_ft',
      'status',
      'year_built',
      'management_type',
      'tkc_number',
      'updated_at',
    ])
    .default('name'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  // Bounded so a hostile page number cannot force an absurd OFFSET scan.
  page: z.coerce.number().int().min(1).max(2000).default(1),
  /**
   * Rows per page (X-6.3). Coerced to a legal size by the service rather than
   * rejected here, because the value arrives from a STORED preference that can
   * outlive the set it was legal in — and a 400 would then break the whole list
   * for whoever held the retired value. Absent means DEFAULT_PAGE_SIZE.
   */
  pageSize: z.coerce.number().int().optional(),
});
export type PropertyListQuery = z.infer<typeof propertyListQuerySchema>;

/** Server page size (docs/03 §3: server pagination at >=50 rows). */
/**
 * The DEFAULT page size for this list. One number for the whole app since
 * X-6.3 made the size a per-user choice — a second literal here would be a
 * second answer to "how many rows does a page hold when nobody chose".
 */
export const PROPERTY_PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * One row of the list. Every field the properties column catalog can show is
 * present on every row, whichever columns the user has chosen: at TKC's scale a
 * page is 50 rows of short strings, and a static projection keeps the query,
 * the sort whitelist and the cache key simple. If a future list needs columns
 * expensive enough to fetch conditionally, that is the moment to make the
 * projection dynamic — not before.
 */
export interface PropertySummary {
  readonly id: string;
  readonly name: string;
  readonly city: string | null;
  readonly state: string | null;
  readonly sqFt: number | null;
  readonly typeName: string | null;
  readonly status: 'active' | 'inactive';
  readonly updatedAt: string;
  readonly isFavorite: boolean;
  readonly canEdit: boolean;
  /**
   * The PRIMARY address, flattened for display (0040). Read-only: the row
   * itself is written through /api/v1/addresses. `primaryAddress()` in
   * contracts/address.ts states the same choice for callers holding the full
   * list, so a list row and a detail card cannot disagree about which address
   * a property "has".
   */
  readonly streetLine: string | null;
  readonly addressLine2: string | null;
  readonly postalCode: string | null;
  readonly yearBuilt: number | null;
  readonly managementTypeName: string | null;
  readonly groupName: string | null;
  readonly yardiId: string | null;
  readonly tkcNumber: string | null;
  /** Content hash of the current photo, or null. Part of the image URL. */
  readonly photoKey: string | null;
  /**
   * Identity of the current RENDITION (punch 4.3). NULL on a pre-4.3 photo,
   * which is served from the legacy storage layout — see `propertyPhotoUrl`.
   */
  readonly photoDerivedKey: string | null;
}

export interface PropertyDetail extends PropertySummary {
  /** Every address and number on the property, not just the primary (0040). */
  readonly addresses: readonly AddressRow[];
  readonly phones: readonly PhoneRow[];
  /** IANA zone, or null for NOT STATED — never a default standing in for one. */
  readonly timeZone: string | null;
  readonly yearBuilt: number | null;
  readonly typeId: string | null;
  /** As on `GroupDetail` (CR-012): false for a kept inactive type, null when none. */
  readonly typeActive: boolean | null;
  readonly managementTypeId: string | null;
  readonly managementTypeName: string | null;
  readonly managementTypeActive: boolean | null;
  readonly groupId: string | null;
  readonly groupName: string | null;
  readonly yardiId: string | null;
  readonly tkcNumber: string | null;
  readonly photoKey: string | null;
  /**
   * Identity of the current RENDITION (punch 4.3). NULL on a pre-4.3 photo,
   * which is served from the legacy storage layout — see `propertyPhotoUrl`.
   */
  readonly photoDerivedKey: string | null;
  /**
   * The framing the current rendition was cut from, and the source it was cut
   * from, so the cropper REOPENS where the last edit left off (RM-029).
   *
   * EDITOR-ONLY: all four are null for a caller without edit scope on this
   * property, whatever the row holds. They are also all null together on a
   * legacy (pre-4.3) photo, whose stored rectangle is in an unknown space —
   * `photoCropSpaceVersion === 1` is the ONLY signal that `photoCrop` may be
   * reopened literally; anything else means open centred.
   */
  readonly photoCrop: PhotoCropRect | null;
  readonly photoSourceWidth: number | null;
  readonly photoSourceHeight: number | null;
  readonly photoCropSpaceVersion: number | null;
  readonly version: number;
  readonly spaces: readonly SpaceRow[];
  readonly tenancies: readonly PropertyTenancyRow[];

  // --- The v1 field set (O-21) ---------------------------------------------
  readonly entityId: string | null;
  readonly tkcWorkUrl: string | null;
  readonly buildingHoursNotes: string | null;
  readonly lockboxLocation: string | null;
  readonly accessNotes: string | null;
  readonly roofHatchNotes: string | null;
  readonly criticalInformation: string | null;
  readonly securityServiceNotes: string | null;
  readonly securityCameras: string | null;
  readonly accessControlSystem: string | null;
  readonly accessControlManagedBy: PartyRef | null;
  readonly alarmMonitoringService: PartyRef | null;
  /** Manual and derived together, derived last. Never a value, just names. */
  readonly aliases: readonly PropertyAlias[];
  /** Only the days the building is open. A missing day is a closed day. */
  readonly hours: readonly HoursDay[];
  /** Only the holidays that are special here. A missing one follows the grid. */
  readonly holidayHours: readonly HolidayHours[];
}

/**
 * A tenancy as the PROPERTY sees it: the Occupancy tab's row, and the
 * reactivate dialog's checklist entry (ADR-41). The org-side view lives in
 * contracts/tenants.ts.
 */
export interface PropertyTenancyRow {
  readonly id: string;
  readonly orgId: string;
  readonly orgName: string;
  readonly status: 'pending' | 'active' | 'inactive';
  readonly moveIn: string | null;
  readonly moveOut: string | null;
  readonly inactivatedViaProperty: boolean;
  readonly spaceLabels: readonly string[];
  readonly version: number;

  /**
   * The ACTIVE lease's term, on the property's own row (0041).
   *
   * Matt, 2026-08-24: "The lease dates are critical for property managers and
   * service coordinators to keep track of and know if a lease is getting ready
   * to expire or needs to be renewed." A lease belongs to one tenancy, and a
   * tenancy to exactly one property, so the property has always been entitled
   * to this — it simply was not queried, and answering "what expires here this
   * year?" meant opening every tenancy one at a time.
   *
   * Null when the tenancy has no active lease: a pending tenancy, or one whose
   * lease is still a draft. `leaseStatus` says which.
   */
  readonly leaseId: string | null;
  readonly leaseStatus: 'draft' | 'active' | 'superseded' | 'inactive' | null;
  readonly commencement: string | null;
  readonly expiration: string | null;
  /**
   * Rentable square feet from that same lease (0044).
   *
   * Matt, 2026-08-25: square footage is "an important fact about tenants and
   * lease data", and the chain is property -> tenant -> lease -> SF. So the
   * figure is the LEASE's and it reads here, on the tenant's row at the
   * property, rather than requiring a click through to find out how big
   * somebody is.
   */
  readonly rentableSqFt: number | null;
}

export interface SpaceRow {
  readonly id: string;
  readonly label: string;
  readonly sqFt: number | null;
  readonly floor: string | null;
  readonly status: 'active' | 'inactive';
  /**
   * True when the space went inactive BECAUSE its property did (ADR-41). The
   * reactivate dialog default-checks these and leaves individually retired
   * spaces unchecked. Always false on an active space.
   */
  readonly inactivatedViaProperty: boolean;
  readonly version: number;
}

/** The duplicate-prevention review payload (docs/02): "Did you mean…". */
export interface DuplicateMatch {
  readonly id: string;
  readonly name: string;
  readonly city: string | null;
  readonly status: 'active' | 'inactive';
  readonly similarity: number;
}
